/**
 * Títulos e trilhas de navegação por rota.
 *
 * O `getPageTitle` do MainLayout só reconhecia caminhos exatos presentes nos menus:
 * qualquer rota com ID (`/pacientes/12`, `/sessoes/5/evolucao`) caía no rótulo
 * genérico "SigaPsi", tanto no título quanto na trilha. Aqui as rotas são descritas
 * por padrão, com trilha contextual — ex.: Pacientes › Maria › Evolução.
 *
 * `rotulos` traz nomes carregados em tempo de execução (nome do paciente, por
 * exemplo), fornecidos pela própria página via `registrarRotuloDeRota`.
 */

// Padrões na ordem de especificidade: o primeiro que casar vence.
const ROTAS = [
    { re: /^\/$/, trilha: () => [{ texto: 'Hoje' }] },
    { re: /^\/dashboard$/, trilha: () => [{ texto: 'Hoje' }] },

    // Pacientes
    { re: /^\/pacientes$/, trilha: () => [{ texto: 'Pacientes' }] },
    { re: /^\/pacientes\/novo$/, trilha: () => [{ texto: 'Pacientes', path: '/pacientes' }, { texto: 'Novo paciente' }] },
    {
        re: /^\/pacientes\/(\d+)\/ficha$/,
        trilha: (m, r) => [{ texto: 'Pacientes', path: '/pacientes' }, { texto: r[`paciente:${m[1]}`] || `Paciente #${m[1]}` }],
    },
    {
        re: /^\/pacientes\/(\d+)$/,
        trilha: (m, r) => [
            { texto: 'Pacientes', path: '/pacientes' },
            { texto: r[`paciente:${m[1]}`] || `Paciente #${m[1]}`, path: `/pacientes/${m[1]}/ficha` },
            { texto: 'Cadastro' },
        ],
    },
    {
        re: /^\/pacientes\/(\d+)\/anexos$/,
        trilha: (m, r) => [
            { texto: 'Pacientes', path: '/pacientes' },
            { texto: r[`paciente:${m[1]}`] || `Paciente #${m[1]}`, path: `/pacientes/${m[1]}/ficha` },
            { texto: 'Anexos' },
        ],
    },

    // Sessões e evolução
    { re: /^\/sessoes$/, trilha: () => [{ texto: 'Atendimentos' }] },
    { re: /^\/sessoes\/novo$/, trilha: () => [{ texto: 'Atendimentos', path: '/sessoes' }, { texto: 'Novo atendimento' }] },
    {
        re: /^\/sessoes\/(\d+)\/evolucao$/,
        trilha: (m, r) => [
            { texto: 'Atendimentos', path: '/sessoes' },
            { texto: r[`sessao:${m[1]}`] || `Atendimento #${m[1]}` },
            { texto: 'Evolução' },
        ],
    },
    {
        re: /^\/sessoes\/(\d+)$/,
        trilha: (m, r) => [
            { texto: 'Atendimentos', path: '/sessoes' },
            { texto: r[`sessao:${m[1]}`] || `Atendimento #${m[1]}` },
        ],
    },

    // Prontuário e anamnese
    {
        re: /^\/prontuarios\/(\d+)$/,
        trilha: (m, r) => [{ texto: 'Prontuários', path: '/prontuarios' }, { texto: r[`prontuario:${m[1]}`] || `Prontuário #${m[1]}` }],
    },
    { re: /^\/prontuarios$/, trilha: () => [{ texto: 'Prontuários' }] },
    {
        re: /^\/anamnese\/(\d+)$/,
        trilha: (m, r) => [{ texto: 'Anamneses', path: '/anamnese' }, { texto: r[`anamnese:${m[1]}`] || `Anamnese #${m[1]}` }],
    },
    { re: /^\/anamnese\/novo$/, trilha: () => [{ texto: 'Anamneses', path: '/anamnese' }, { texto: 'Nova anamnese' }] },
    { re: /^\/anamnese$/, trilha: () => [{ texto: 'Anamneses' }] },

    // Financeiro
    { re: /^\/financeiro$/, trilha: () => [{ texto: 'Financeiro' }] },
    { re: /^\/financeiro\/historico$/, trilha: () => [{ texto: 'Financeiro', path: '/financeiro' }, { texto: 'Histórico' }] },
    { re: /^\/contas-receber$/, trilha: () => [{ texto: 'Financeiro', path: '/financeiro' }, { texto: 'Contas a receber' }] },
    {
        re: /^\/contas-receber\/(novo|\d+)$/,
        trilha: (m) => [
            { texto: 'Financeiro', path: '/financeiro' },
            { texto: 'Contas a receber', path: '/contas-receber' },
            { texto: m[1] === 'novo' ? 'Novo título' : `Título #${m[1]}` },
        ],
    },
    { re: /^\/contas-pagar$/, trilha: () => [{ texto: 'Financeiro', path: '/financeiro' }, { texto: 'Contas a pagar' }] },
    {
        re: /^\/contas-pagar\/(novo|\d+)$/,
        trilha: (m) => [
            { texto: 'Financeiro', path: '/financeiro' },
            { texto: 'Contas a pagar', path: '/contas-pagar' },
            { texto: m[1] === 'novo' ? 'Nova despesa' : `Despesa #${m[1]}` },
        ],
    },
    { re: /^\/conta-bancaria$/, trilha: () => [{ texto: 'Financeiro', path: '/financeiro' }, { texto: 'Contas bancárias' }] },
    {
        re: /^\/conta-bancaria\/(novo|\d+)$/,
        trilha: (m) => [
            { texto: 'Financeiro', path: '/financeiro' },
            { texto: 'Contas bancárias', path: '/conta-bancaria' },
            { texto: m[1] === 'novo' ? 'Nova conta' : `Conta #${m[1]}` },
        ],
    },
    { re: /^\/plano-pagamento$/, trilha: () => [{ texto: 'Financeiro', path: '/financeiro' }, { texto: 'Planos de pagamento' }] },
    {
        re: /^\/plano-pagamento\/(novo|\d+)$/,
        trilha: (m) => [
            { texto: 'Financeiro', path: '/financeiro' },
            { texto: 'Planos de pagamento', path: '/plano-pagamento' },
            { texto: m[1] === 'novo' ? 'Novo plano' : `Plano #${m[1]}` },
        ],
    },

    // Convênios
    { re: /^\/convenios$/, trilha: () => [{ texto: 'Convênios' }] },
    { re: /^\/convenios\/novo$/, trilha: () => [{ texto: 'Convênios', path: '/convenios' }, { texto: 'Novo convênio' }] },
    {
        re: /^\/convenios\/(\d+)\/procedimentos$/,
        trilha: (m, r) => [
            { texto: 'Convênios', path: '/convenios' },
            { texto: r[`convenio:${m[1]}`] || `Convênio #${m[1]}`, path: `/convenios/${m[1]}` },
            { texto: 'Tabela de valores' },
        ],
    },
    {
        re: /^\/convenios\/(\d+)$/,
        trilha: (m, r) => [
            { texto: 'Convênios', path: '/convenios' },
            { texto: r[`convenio:${m[1]}`] || `Convênio #${m[1]}` },
        ],
    },
    {
        re: /^\/pacientes\/(\d+)\/convenios$/,
        trilha: (m, r) => [
            { texto: 'Pacientes', path: '/pacientes' },
            { texto: r[`paciente:${m[1]}`] || `Paciente #${m[1]}`, path: `/pacientes/${m[1]}/ficha` },
            { texto: 'Convênios' },
        ],
    },
    { re: /^\/guias$/, trilha: () => [{ texto: 'Convênios', path: '/convenios' }, { texto: 'Guias' }] },
    { re: /^\/guias\/novo$/, trilha: () => [{ texto: 'Convênios', path: '/convenios' }, { texto: 'Guias', path: '/guias' }, { texto: 'Emitir guia' }] },
    { re: /^\/lotes-faturamento$/, trilha: () => [{ texto: 'Convênios', path: '/convenios' }, { texto: 'Lotes de faturamento' }] },
    {
        re: /^\/lotes-faturamento\/(\d+)$/,
        trilha: (m) => [
            { texto: 'Convênios', path: '/convenios' },
            { texto: 'Lotes de faturamento', path: '/lotes-faturamento' },
            { texto: `Lote #${m[1]}` },
        ],
    },

    // Assinaturas e planos de atendimento
    { re: /^\/assinaturas-paciente$/, trilha: () => [{ texto: 'Assinaturas' }] },
    { re: /^\/planos-atendimento$/, trilha: () => [{ texto: 'Planos de atendimento' }] },
    { re: /^\/periodicidades$/, trilha: () => [{ texto: 'Periodicidades' }] },
    { re: /^\/assinaturas-paciente\/renovacoes$/, trilha: () => [{ texto: 'Assinaturas', path: '/assinaturas-paciente' }, { texto: 'Renovações' }] },

    // Agenda
    { re: /^\/agendamentos\/calendario$/, trilha: () => [{ texto: 'Agenda' }] },
    { re: /^\/agendamentos\/disponibilidade$/, trilha: () => [{ texto: 'Agenda', path: '/agendamentos/calendario' }, { texto: 'Disponibilidade' }] },
    { re: /^\/agendamentos\/fila-espera$/, trilha: () => [{ texto: 'Agenda', path: '/agendamentos/calendario' }, { texto: 'Fila de espera' }] },

    // Documentos
    { re: /^\/documentos\/gerar$/, trilha: () => [{ texto: 'Documentos' }, { texto: 'Gerar' }] },
    { re: /^\/documentos\/historico$/, trilha: () => [{ texto: 'Documentos' }, { texto: 'Histórico' }] },
    { re: /^\/documentos\/modelos$/, trilha: () => [{ texto: 'Documentos' }, { texto: 'Modelos' }] },

    // Relatórios
    {
        re: /^\/relatorios\/([a-z-]+)$/,
        trilha: (m) => [{ texto: 'Relatórios' }, { texto: nomeRelatorio(m[1]) }],
    },
    { re: /^\/relatorios$/, trilha: () => [{ texto: 'Relatórios' }] },

    // Configurações
    { re: /^\/parametros$/, trilha: () => [{ texto: 'Configurações' }, { texto: 'Parâmetros' }] },
    { re: /^\/parametros-contrato$/, trilha: () => [{ texto: 'Configurações' }, { texto: 'Contrato' }] },
    { re: /^\/usuarios$/, trilha: () => [{ texto: 'Configurações' }, { texto: 'Usuários' }] },
    { re: /^\/status-sistema$/, trilha: () => [{ texto: 'Configurações' }, { texto: 'Status do sistema' }] },
    { re: /^\/psicologos$/, trilha: () => [{ texto: 'Configurações' }, { texto: 'Profissionais' }] },
    { re: /^\/perfil$/, trilha: () => [{ texto: 'Meu perfil' }] },
    { re: /^\/notificacoes\/historico$/, trilha: () => [{ texto: 'Notificações' }, { texto: 'Histórico' }] },
];

function nomeRelatorio(slug) {
    const nomes = {
        psicologos: 'Psicólogos',
        pacientes: 'Pacientes',
        'contas-receber': 'Contas a receber',
        'contas-pagar': 'Contas a pagar',
        sessoes: 'Atendimentos',
    };
    return nomes[slug] || slug;
}

/**
 * Rótulos dinâmicos (nome do paciente etc.). Guardados só em memória: são cache de
 * apresentação, não dado de sessão.
 */
const rotulos = {};

export const registrarRotuloDeRota = (chave, texto) => {
    if (chave && texto) rotulos[chave] = texto;
};

export const limparRotulosDeRota = () => {
    Object.keys(rotulos).forEach((k) => delete rotulos[k]);
};

/** Trilha da rota: lista de { texto, path? }, do mais geral ao mais específico. */
export const trilhaDaRota = (pathname) => {
    for (const rota of ROTAS) {
        const m = pathname.match(rota.re);
        if (m) return rota.trilha(m, rotulos);
    }
    // Rota não mapeada: usa os segmentos da URL em vez de cair em "SigaPsi".
    const segmentos = pathname.split('/').filter(Boolean);
    if (segmentos.length === 0) return [{ texto: 'Hoje' }];
    return segmentos.map((s) => ({ texto: humanizar(s) }));
};

/** Título da página = último item da trilha. */
export const tituloDaRota = (pathname) => {
    const trilha = trilhaDaRota(pathname);
    return trilha.length ? trilha[trilha.length - 1].texto : 'SigaPsi';
};

function humanizar(segmento) {
    if (/^\d+$/.test(segmento)) return `#${segmento}`;
    return segmento
        .replace(/-/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase());
}
